import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "flex-1 flex items-stretch overflow-hidden" }
const _hoisted_2 = { class: "flex-1 overflow-y-auto" }
const _hoisted_3 = {
  class: "flex justify-center items-center pb-2",
  style: {"min-height":"56px"}
}
const _hoisted_4 = ["disabled"]
const _hoisted_5 = {
  key: 1,
  class: "pl-8 pr-8 2xl:pl-12 2xl:pr-12 text-sm 2xl:text-lg font-extrabold text-oaiGray-300"
}
const _hoisted_6 = ["value", "disabled", "max"]
const _hoisted_7 = ["disabled"]
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { class: "grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 md:grid-cols-3 lg:grid-cols-3 2xl:grid-cols-4 xl:gap-x-8" }
const _hoisted_10 = /*#__PURE__*/_createElementVNode("div", { class: "image group block w-full aspect-w-10 aspect-h-7 rounded-lg bg-gray-50 overflow-hidden" }, null, -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("h2", { class: "mt-2 block h-xs font-medium text-gray-900 rounded-xs w-10" }, null, -1)
const _hoisted_12 = [
  _hoisted_10,
  _hoisted_11
]
const _hoisted_13 = { key: 1 }
const _hoisted_14 = { class: "grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 md:grid-cols-3 lg:grid-cols-3 2xl:grid-cols-4 xl:gap-x-8 pb-8" }
const _hoisted_15 = ["src"]
const _hoisted_16 = ["id"]
const _hoisted_17 = ["href", "data-pswp-width", "data-pswp-height", "data-timestamp"]
const _hoisted_18 = { class: "group block w-full aspect-w-10 aspect-h-7 rounded-lg bg-gray-100 overflow-hidden" }
const _hoisted_19 = ["src"]
const _hoisted_20 = { class: "mt-2 block text-xs font-medium text-gray-900" }

import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/24/solid";
import { format, isValid } from "date-fns";
import { Padding } from "photoswipe";
import { PhotoSwipe } from "photoswipe/dist/types/ui/ui-element";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import { computed, onMounted, onUnmounted, Ref, ref, watch } from "vue";
import defaultProjectThumbnailUrl from "shared/assets/default-project-thumbnail.jpg";
import { useCurrentCustomerName, useCurrentSiteId } from "shared/composables/project";
import { useCustomToast } from "shared/composables/toast";
import CameraRepository from "shared/repositories/CameraRepository";
import logger from "shared/services/logger";
import { GalleryImagesPaginatedResponse } from "shared/types/Camera";


export default /*#__PURE__*/_defineComponent({
  __name: 'GalleryContentImages',
  props: {
    enableHeaderDateField: { type: Boolean },
    date: {},
    data: {},
    loading: { type: Boolean },
    showDefaultImages: { type: Boolean },
    cameraId: {},
    hasAdminPermissions: { type: Boolean }
  },
  emits: ["dateChange"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const lightbox = ref(null) as Ref<PhotoSwipeLightbox | null>;

const customer = useCurrentCustomerName();
const site = useCurrentSiteId();
const toast = useCustomToast();

onUnmounted(() => {
  if (lightbox.value) {
    lightbox.value.destroy();
    lightbox.value = null;
  }
});

const galleryId = computed(() => `gallery-${customer}-${site}-${props.cameraId}`);

const maxDate = computed(() => new Date());

const mountPhotoSwipe = () => {
  if (!lightbox.value) {
    const lightboxInstance = new PhotoSwipeLightbox({
      gallery: `#${galleryId.value}`,
      children: "a",
      pswpModule: () => import("photoswipe"),
      wheelToZoom: true,
      padding: { top: 10, bottom: 10 } as Padding,
    });
    lightboxInstance.on("uiRegister", () => {
      lightboxInstance.pswp?.ui?.registerElement({
        name: "download-button",
        order: 8,
        isButton: true,
        tagName: "a",

        // SVG with outline
        html: {
          isCustomSVG: true,
          inner:
            '<path d="M20.5 14.3 17.1 18V10h-2.2v7.9l-3.4-3.6L10 16l6 6.1 6-6.1-1.5-1.6ZM23 23H9v2h14" id="pswp__icn-download"/>',
          outlineID: "pswp__icn-download",
        },

        onInit: (el: HTMLElement, pswp: PhotoSwipe) => {
          el.setAttribute("download", "");
          el.setAttribute("target", "_blank");
          el.setAttribute("rel", "noopener");

          pswp.on("change", () => {
            el.setAttribute("href", pswp.currSlide?.data.src as string);
          });
        },
      });

      if (props.hasAdminPermissions) {
        lightboxInstance.pswp?.ui?.registerElement({
          name: "annotation-button",
          order: 7,
          isButton: true,
          tagName: "p",
          className: "!w-auto !flex !items-center",
          html: '<p class="px-3 py-2 rounded-md bg-yellow-600 text-white text-xs whitespace-nowrap">Add for CV Dataset</p>',

          onClick(e, el, pswp) {
            const timestamp = pswp.currSlide?.data?.element?.dataset?.timestamp;

            if (!timestamp) {
              return;
            }
            const formattedTimestamp = formatTimestampForPersonDataset(timestamp);

            CameraRepository.addPersonImageToDatasetBuffer(
              customer as string,
              site as string,
              props.cameraId,
              formattedTimestamp,
            )
              .then(() => {
                toast.success("Image successfully uploaded");
              })
              .catch((error) => {
                logger.error(error);
                toast.error("Failed to upload image");
              });
          },
        });
      }
    });

    lightboxInstance.init();
    lightbox.value = lightboxInstance;
  }
};

const displayTime = (value: string) => {
  const date = new Date(value);
  return isValid(date) ? format(new Date(value), "HH:mm") : "";
};

const handleHeaderDatePickerChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit("dateChange", target.value);
};

const formatDate = (dateValue: string | Date) => {
  const dateObject = typeof dateValue === "string" ? new Date(dateValue) : dateValue;
  return format(dateObject, "dd.MM.yyyy");
};

const formatTimestampForPersonDataset = (timestamp: string) =>
  format(new Date(timestamp), "yyyy-MM-dd_HHmmss");

watch(
  () => props.data,
  () => {
    mountPhotoSwipe();
  },
);

onMounted(() => {
  mountPhotoSwipe();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("main", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        (!_ctx.loading)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('dateChange', _ctx.data?.previous ?? null))),
              type: "button",
              disabled: !(_ctx.data && _ctx.data.previous),
              class: "border-transparent inline-flex items-center px-4 py-2 text-sm font-medium"
            }, [
              _createVNode(_unref(ChevronLeftIcon), {
                class: _normalizeClass(["h-6 w-6 text-oaiGray-300", { 'text-transparent': !(_ctx.data && _ctx.data.previous) }]),
                "aria-hidden": "true"
              }, null, 8, ["class"])
            ], 8, _hoisted_4))
          : _createCommentVNode("", true),
        (_ctx.date && !_ctx.enableHeaderDateField)
          ? (_openBlock(), _createElementBlock("h2", _hoisted_5, _toDisplayString(formatDate(_ctx.date)), 1))
          : _createCommentVNode("", true),
        (_ctx.date && _ctx.enableHeaderDateField)
          ? (_openBlock(), _createElementBlock("input", {
              key: 2,
              type: "date",
              value: _unref(format)(_ctx.date, 'yyyy-MM-dd'),
              onChange: handleHeaderDatePickerChange,
              disabled: _ctx.loading,
              max: _unref(format)(maxDate.value, 'yyyy-MM-dd'),
              onKeydown: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"])),
              class: "w-full rounded-md border-gray-300 text-xs shadow-sm focus:border-yellow-500 focus:ring-yellow-500",
              style: {"width":"min-content !important"}
            }, null, 40, _hoisted_6))
          : _createCommentVNode("", true),
        (!_ctx.loading)
          ? (_openBlock(), _createElementBlock("button", {
              key: 3,
              onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('dateChange', _ctx.data?.next ?? null))),
              type: "button",
              disabled: !(_ctx.data && _ctx.data.next),
              class: "border-transparent inline-flex items-center px-4 py-2 text-sm font-medium"
            }, [
              _createVNode(_unref(ChevronRightIcon), {
                class: _normalizeClass(["h-6 w-6 text-oaiGray-300", { 'text-transparent': !(_ctx.data && _ctx.data.next) }]),
                "aria-hidden": "true"
              }, null, 8, ["class"])
            ], 8, _hoisted_7))
          : _createCommentVNode("", true)
      ]),
      (_ctx.loading)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createElementVNode("div", _hoisted_9, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(4, (i) => {
                return _createElementVNode("div", {
                  class: "loadingCard is-loading",
                  key: i
                }, _hoisted_12)
              }), 64))
            ])
          ]))
        : _createCommentVNode("", true),
      (!_ctx.loading && _ctx.showDefaultImages)
        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
            _createElementVNode("div", _hoisted_14, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(4, (i) => {
                return _createElementVNode("div", { key: i }, [
                  _createElementVNode("img", {
                    src: _unref(defaultProjectThumbnailUrl),
                    alt: "default image",
                    class: "group block w-full rounded-lg bg-gray-50 overflow-hidden"
                  }, null, 8, _hoisted_15)
                ])
              }), 64))
            ])
          ]))
        : _createCommentVNode("", true),
      _withDirectives(_createElementVNode("div", {
        id: galleryId.value,
        class: "grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 md:grid-cols-3 lg:grid-cols-3 2xl:grid-cols-4 xl:gap-x-8"
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.data?.items, (image, key) => {
          return (_openBlock(), _createElementBlock("div", { key: key }, [
            _createElementVNode("a", {
              href: image.url,
              "data-pswp-width": image.width,
              "data-pswp-height": image.height,
              "data-timestamp": image.timestamp,
              target: "_blank",
              rel: "noreferrer"
            }, [
              _createElementVNode("div", _hoisted_18, [
                _createElementVNode("img", {
                  src: image.url,
                  class: "group-hover:opacity-75 object-cover pointer-events-none",
                  alt: "gallery image"
                }, null, 8, _hoisted_19)
              ])
            ], 8, _hoisted_17),
            _createElementVNode("div", _hoisted_20, _toDisplayString(displayTime(image.timestamp)), 1)
          ]))
        }), 128))
      ], 8, _hoisted_16), [
        [_vShow, !_ctx.loading && _ctx.data]
      ])
    ])
  ]))
}
}

})